import React from "react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const C = {
  primary: "hsl(var(--chart-1))", benchmark: "hsl(var(--chart-2))", positive: "hsl(var(--chart-1))",
  negative: "hsl(var(--chart-5))", grid: "hsl(var(--border))", axis: "hsl(var(--muted-foreground))",
};
export const CHART_COLORS = [
  "hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))",
  "hsl(var(--chart-1)/0.55)", "hsl(var(--chart-2)/0.55)", "hsl(var(--chart-3)/0.5)",
];

// Shared prop objects, defined once: recharts re-renders when a prop identity changes.
const MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const MARGIN_TIGHT = { top: 2, right: 0, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 10, fill: C.axis };
const AUTO_DOMAIN = ["auto", "auto"];

const tooltipStyle = {
  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 14,
  color: "hsl(var(--popover-foreground))", fontSize: 12, fontFamily: "IBM Plex Mono, monospace",
  boxShadow: "0 12px 32px -20px rgba(16,24,40,0.35)",
};

export const AreaTrend = ({ data, dataKey = "value", xKey = "date", color, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={MARGIN}>
      <defs>
        <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color || C.positive} stopOpacity={0.14} />
          <stop offset="100%" stopColor={color || C.positive} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_TICK} minTickGap={40} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS_TICK} width={48} tickLine={false} axisLine={false} domain={AUTO_DOMAIN} />
      <Tooltip contentStyle={tooltipStyle} />
      <Area type="monotone" dataKey={dataKey} stroke={color || C.positive} strokeWidth={2} fill={`url(#grad-${dataKey})`} />
    </AreaChart>
  </ResponsiveContainer>
);

export const LineTrend = ({ data, dataKey = "close", xKey = "date", color, height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data} margin={MARGIN}>
      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_TICK} minTickGap={40} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS_TICK} width={52} tickLine={false} axisLine={false} domain={AUTO_DOMAIN} />
      <Tooltip contentStyle={tooltipStyle} />
      <Line type="monotone" dataKey={dataKey} stroke={color || C.primary} strokeWidth={2} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

export const Sparkline = ({ data, dataKey = "v", color, height = 40 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={MARGIN_TIGHT}>
      <defs>
        <linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color || C.positive} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color || C.positive} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey={dataKey} stroke={color || C.positive} strokeWidth={1.75} fill="url(#spark-grad)" dot={false} isAnimationActive={false} />
    </AreaChart>
  </ResponsiveContainer>
);

export const DonutAllocation = ({ data, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="88%" paddingAngle={3} cornerRadius={6} stroke="none">
        {data.map((entry, i) => <Cell key={entry.name ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
      </Pie>
      <Tooltip contentStyle={tooltipStyle} />
    </PieChart>
  </ResponsiveContainer>
);

export const BarBreakdown = ({ data, dataKey = "value", xKey = "name", height = 260 }) => (  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={data} margin={MARGIN}>
      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS_TICK} width={48} tickLine={false} axisLine={false} />
      <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--accent))" }} />
      <Bar dataKey={dataKey} radius={[8, 8, 0, 0]}>
        {data.map((entry, i) => <Cell key={entry.name ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

export const ValueVsContributions = ({ data, height = 300 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={MARGIN}>
      <defs>
        <linearGradient id="grad-value" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={C.positive} stopOpacity={0.30} />
          <stop offset="100%" stopColor={C.positive} stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
      <XAxis dataKey="date" tick={AXIS_TICK} minTickGap={48} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS_TICK} width={56} tickLine={false} axisLine={false} domain={AUTO_DOMAIN}
        tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
      <Tooltip contentStyle={tooltipStyle}
        formatter={(v, k) => [`$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
          k === "value" ? "Portfolio value" : "Net deposits"]} />
      <Area type="monotone" dataKey="value" stroke={C.positive} strokeWidth={2.25} fill="url(#grad-value)" isAnimationActive={false} />
      <Area type="monotone" dataKey="contributed" stroke={C.benchmark} strokeWidth={2} strokeDasharray="5 4"
        fill="none" isAnimationActive={false} />
    </AreaChart>
  </ResponsiveContainer>
);

export const MIX_CLASS_COLORS = {
  stock: "hsl(var(--chart-1))", commodity: "hsl(var(--chart-2))", forex: "hsl(var(--chart-3))",
  crypto: "hsl(var(--chart-4))", cash: "hsl(var(--muted-foreground))", savings: "hsl(var(--chart-1)/0.55)",
};

export const StackedAreaMix = ({ data, keys = [], xKey = "date", height = 280, labels = {} }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data} margin={MARGIN}>
      <defs>
        {keys.map((k) => (
          <linearGradient key={k} id={`mix-grad-${k}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={MIX_CLASS_COLORS[k] || "hsl(var(--muted-foreground))"} stopOpacity={0.55} />
            <stop offset="100%" stopColor={MIX_CLASS_COLORS[k] || "hsl(var(--muted-foreground))"} stopOpacity={0.12} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
      <XAxis dataKey={xKey} tick={AXIS_TICK} minTickGap={40} tickLine={false} axisLine={false} />
      <YAxis tick={AXIS_TICK} width={52} tickLine={false} axisLine={false} domain={AUTO_DOMAIN} />
      <Tooltip contentStyle={tooltipStyle} formatter={(v, k) => [`$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`, labels[k] || k]} />
      {keys.map((k) => (
        <Area key={k} type="monotone" dataKey={k} stackId="mix"
          stroke={MIX_CLASS_COLORS[k] || "hsl(var(--muted-foreground))"} strokeWidth={1.5}
          fill={`url(#mix-grad-${k})`} isAnimationActive={false} />
      ))}
    </AreaChart>
  </ResponsiveContainer>
);
